import React, { useState, useEffect, createContext, useContext } from 'react';
import toast from 'react-hot-toast';
import { logActivity } from '../utils/activityLogger';
import tokenManager from '../utils/tokenManager';
import { Card, Body1, Title1 } from '@fluentui/react-components';

// Google Client ID - you'll need to set this
const GOOGLE_CLIENT_ID = '440720054274-ngrqanv8repk72q024o3c0qaid534752.apps.googleusercontent.com';

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleLoaded, setGoogleLoaded] = useState(false);

  useEffect(() => {
    // Load Google Identity Services
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => {
      setGoogleLoaded(true);
      // Initialize Google Sign-In
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        use_fedcm_for_prompt: false
      });
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    // Check if auth is bypassed
    fetch('/api/auth/verify')
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Auth check failed');
      })
      .then(data => {
        if (data.user) {
          setUser(data.user);
        }
        setLoading(false);
      })
      .catch(() => {
        // Check if user is already logged in with valid token
        const savedToken = tokenManager.getToken();
        if (savedToken) {
          // Verify token with backend
          verifyTokenWithBackend(savedToken);
        } else {
          setLoading(false);
        }
      });
  }, []);

  const handleCredentialResponse = async (response) => {
    try {
      const token = response.credential;
      
      // Save token with expiry tracking
      const saved = tokenManager.saveToken(token);
      if (!saved) {
        throw new Error('Failed to save token');
      }
      
      // Verify with backend
      await verifyTokenWithBackend(token);
      
      toast.success('Successfully signed in!');
    } catch (error) {
      console.error('Sign in error:', error);
      
      toast.error('Sign in failed');
      tokenManager.clearToken();
    }
  };

  const verifyTokenWithBackend = async (token) => {
    try {
      const backendUrl = '';
      const response = await fetch(`${backendUrl}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData.user);
      } else {
        throw new Error('Token verification failed');
      }
    } catch (error) {
      console.error('Token verification error:', error);
      tokenManager.clearToken();
      
      toast.error('Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const signIn = () => {
    if (googleLoaded && window.google) {
      window.google.accounts.id.prompt();
    }
  };

  const bypassAuth = () => {
    console.log('Bypassing authentication for localhost development');
    setUser({
      email: 'admin@localhost',
      name: 'Admin User (Bypassed)',
      verified: true
    });
    toast.success('Signed in (Development Mode)');
  };

  const signOut = async () => {
    try {
      // Log logout activity before removing token
      await logActivity('logout', 'user', null, 'User logged out');
    } catch (error) {
      console.error('Failed to log logout activity:', error);
    }
    
    tokenManager.clearToken();
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
    toast.success('Signed out successfully');
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
    googleLoaded,
    bypassAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const LoginScreen = () => {
  const { signIn, googleLoaded, bypassAuth } = useAuth();

  useEffect(() => {
    if (googleLoaded) {
      // Render the Google Sign-In button
      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        {
          theme: 'outline',
          size: 'large',
          text: 'sign_in_with',
          width: 300
        }
      );
    }
  }, [googleLoaded]);

  return (
    <div className="login-screen">
      <Card className="login-card">
        <Title1>Sander Eriksen</Title1>
        <Body1>Please sign in to continue</Body1>
        
        <div className="login-provider-button">
          <div id="google-signin-button"></div>
        </div>
        
        {window.location.hostname === 'localhost' && (
          <div className="login-dev-mode">
            <p>
              Development Mode
            </p>
            <button className="btn btn-secondary" onClick={bypassAuth}>
              Skip Authentication
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};
